import { Navigate, createBrowserRouter } from 'react-router-dom';

import HomePage from '../features/home/pages/HomePage';
import LandingPage from '../features/home/pages/LandingPage';
import LoginPage from '../features/auth/pages/LoginPage';
import SignupPage from '../features/auth/pages/SignupPage';
import ForgotPasswordPage from '../features/auth/pages/ForgotPasswordPage';
import ResetPasswordPage from '../features/auth/pages/ResetPasswordPage';
import ErrorPage from '../shared/components/ErrorPage';
import Layout from '../shared/components/Layout';
import MyCourses from '../features/my-courses/MyCoursesPage';
import MyRubrics from '../features/rubrics/myRubricsPage';
import StandardsPage from '../features/customStandards/customStandardsPage';
import StandardsLibraryShell from '../features/userStandards/StandardsLibraryShell';
import MyLibraryView from '../features/userStandards/views/MyLibraryView';
import FindStandardsView from '../features/userStandards/views/FindStandardsView';
import CoursePage from '../features/courses/CoursePage';
import CurriculumLayout from '../features/courses/components/CurriculumLayout';
import UnitPage from '../features/units/UnitPage';
import LessonPage from '../features/lessons/LessonPage';
import PrivateRoute from '../shared/components/PrivateRoute';
import PublicRoute from '../shared/components/PublicRoute';
import TeamsPage from '../features/teams/TeamsPage';
import ResourceDocumentEditorPage from '../features/resources/ResourceDocumentEditorPage';
import ResourceSlideEditorPage from '../features/slides/ResourceSlideEditorPage';
import FileResourceViewPage from '../features/resources/FileResourceViewPage';
import ResourceRubricEditorPage from '../features/resources/ResourceRubricEditorPage';
import RubricEditorPage from '../features/rubrics/RubricEditorPage';
import GoogleWorkspaceOAuthCallbackPage from '../features/resources/GoogleWorkspaceOAuthCallbackPage';

import { isFeatureEnabled } from '@/shared/libs/featureFlags';

// Root layout component that wraps all routes
const RootLayout = () => {
  return <Layout showHeader={true} maxWidth={false} />;
};

// Define routes using the Data API
export const router = createBrowserRouter([
  {
    path: '/',
    element: <HomePage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/login',
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: '/signup',
    element: (
      <PublicRoute>
        <SignupPage />
      </PublicRoute>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <PublicRoute>
        <ForgotPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/oauth/google-workspace/callback',
    element: <GoogleWorkspaceOAuthCallbackPage />,
  },
  // ---- protected routes (require authentication) ------------------
  {
    path: '/curriculum',
    element: (
      <PrivateRoute>
        <RootLayout />
      </PrivateRoute>
    ),
    children: [
      {
        index: true,
        element: <LandingPage />,
      },
      {
        path: 'courses',
        element: <MyCourses />,
      },
      {
        path: 'teams',
        element: isFeatureEnabled('teamManagement') ? (
          <TeamsPage />
        ) : (
          <Navigate to="/curriculum" replace />
        ),
      },
      {
        path: 'rubrics',
        element: isFeatureEnabled('rubrics') ? (
          <MyRubrics />
        ) : (
          <Navigate to="/curriculum" replace />
        ),
      },
      {
        path: 'standards',
        element: isFeatureEnabled('standardsLibraryV2') ? (
          <StandardsLibraryShell />
        ) : (
          <StandardsPage />
        ),
        children: isFeatureEnabled('standardsLibraryV2')
          ? [
              { index: true, element: <MyLibraryView /> },
              { path: 'find', element: <FindStandardsView /> },
            ]
          : undefined,
      },
    ],
  },

  // ---- public-viewable detail routes (auth optional) ---------------
  // These use the same /curriculum/* URLs so authenticated users can
  // share their current URL and unauthenticated viewers see a read-only
  // version. RLS enforces visibility (is_public = true); the UI uses
  // canEdit guards to hide edit controls for viewers/anon users.
  {
    path: '/curriculum',
    element: <RootLayout />,
    children: [
      {
        // CurriculumLayout: single drill-down sidebar for course → unit → lesson
        path: 'courses/:courseId',
        element: <CurriculumLayout />,
        children: [
          { index: true, element: <CoursePage /> },
          { path: 'units/:unitId', element: <UnitPage /> },
          { path: 'units/:unitId/lessons/:lessonId', element: <LessonPage /> },
        ],
      },
      {
        path: 'rubrics/:rubricId',
        element: isFeatureEnabled('rubrics') ? (
          <RubricEditorPage />
        ) : (
          <Navigate to="/curriculum" replace />
        ),
      },
      {
        // CurriculumLayout derives courseId from unit.course_id for the back link
        path: 'units/:unitId',
        element: <CurriculumLayout />,
        children: [
          { index: true, element: <UnitPage /> },
          { path: 'lessons/:lessonId', element: <LessonPage /> },
        ],
      },
      {
        path: 'lessons/:lessonId',
        element: <LessonPage />,
      },
      {
        path: 'resources/:resourceId/edit',
        element: <ResourceDocumentEditorPage />,
      },
      {
        path: 'resources/:resourceId/slides',
        element: <ResourceSlideEditorPage />,
      },
      {
        path: 'resources/:resourceId/file',
        element: <FileResourceViewPage />,
      },
      {
        path: 'resources/:resourceId/rubric',
        element: isFeatureEnabled('rubrics') ? (
          <ResourceRubricEditorPage />
        ) : (
          <Navigate to="/curriculum" replace />
        ),
      },
    ],
  },
]);

export default router;
